<template>
  <button ref="button" :class="className" :disabled="disabled" @click="action">
    <template v-if="loading">
      <slot name="loader">
        <!-- default loader -->
      </slot>
    </template>
    <template v-else>
      <slot name="prepend"></slot>
      <slot>Button</slot>
      <slot name="append"></slot>
    </template>
  </button>
</template>

<script>
import { twMerge } from 'tailwind-merge';

const baseClasses =
  'inline-flex items-center justify-center gap-2 focus:outline-none border shadow px-4 py-2 hover:shadow-lg focus:shadow-lg';
const variantClasses = {
  base: 'bg-white text-grey-500 border-white',
  primary:
    'bg-primary text-primary-invert border-primary hover:bg-primary-dark hover:border-primary-dark',
  secondary:
    'bg-white text-dark hover:bg-primary hover:border-primary hover:text-primary-invert',
  dark:
    'bg-secondary text-secondary-invert border-secondary hover:bg-secondary-dark hover:border-secondary-dark',
  transparent:
    'bg-transparent text-grey-500 border-grey-500 hover:bg-primary hover:border-primary hover:text-primary-invert',
  outline: 'bg-transparent border-primary text-primary'
};
const sizeClasses = {
  auto: 'min-w-32 w-auto h-10 px-2',
  expand: 'w-full h-10 flex flex-grow',
  icon: 'w-8 h-8 p-0',
  iconlg: 'w-10 h-10 p-0',
  xs: 'w-8 h-8 p-1 text-xs',
  sm: 'w-16 h-8 p-2 text-sm',
  md: 'w-32 h-10 p-2',
  lg: 'w-48 h-10 px-4 py-2 text-lg',
};
const groupClasses = {
  none: 'rounded-lg',
  left: 'rounded-l-lg',
  middle: 'border-l-0',
  right: 'border-l-0 rounded-r-lg',
};

export default {
  name: 'GdButton',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    stopEvent: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'auto',
      validator: (val) =>
        ['sm', 'md', 'lg', 'icon', 'iconlg', 'auto', 'expand'].includes(val),
    },
    variant: {
      type: String,
      default: 'base',
    },
    groupPosition: {
      type: String,
      default: 'none',
      validator: (val) => ['none', 'left', 'middle', 'right'].includes(val),
    },
    overrideClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    className() {
      const classes = twMerge(
        baseClasses,
        variantClasses[this.variant],
        sizeClasses[this.size],
        groupClasses[this.groupPosition],
        this.overrideClass,
      );
      return this.disabled
        ? `${classes} cursor-not-allowed filter contrast-75`
        : classes;
    },
  },
  methods: {
    action(e) {
      if (this.disabled) return;
      if (this.stopEvent) {
        e.stopPropagation();
      }
      this.$emit('click');
      setTimeout(() => {
        if (this.$refs.button) this.$refs.button.blur();
      }, 500);
    },
  },
};
</script>
